/**
 * Rich mock book-of-business for the Pro CRM preview.
 *
 * These are VIEW MODELS (lib/view-model.ts), not raw wire DTOs — the preview
 * renders the same shapes the prod adapter produces, so the components are
 * identical in dev and prod. The fixtures span:
 *
 *   - all three verification tiers (1 self / 2 doc / 3 bank),
 *   - every funnel state (added → invited → opened → signed_up → linked → unlinked),
 *   - both sharing levels — and the FIELD-PRESENCE CONTRACT is honored: a
 *     `status_only` client's financial fields carry NO magnitudes (only verbs +
 *     "not shared" stamps), mirroring the real status_only projection.
 *
 * NOTE: there is intentionally NO readiness score anywhere in this data.
 */

import type {
  ActivityFeedItem,
  AssistantAllowanceVM,
  BillingStatusVM,
  ClientVM,
  ContactFunnelSummaryVM,
  ContactVM,
  CRMBillingSummaryVM,
  ImportPreviewVM,
  KpiMetric,
  NotificationPrefVM,
  ProProfileVM,
  ProUserVM,
  PersonVM,
  RateCardRowVM,
  SignalEvent,
  SillSuggestion,
} from "./view-model";
import type { MyReferralsResponse } from "./types";
import type { NotificationItem } from "./api";
import { NOTIFICATION_CATALOG } from "./adapt";
import type {
  ConversationVM,
  PinnedOfferVM,
  ThreadMessageVM,
} from "./thread-types";

// The logged-in pro. The role here seeds the role gate in preview; in prod it
// comes from the authenticated user.
export const FIXTURE_PRO: ProUserVM = {
  name: "Jess Soto",
  org: "Windermere · Ballard",
  role: "lo",
  avatar: "JS",
};

/** Preview-only owner-scoped referral history; intentionally has no referee PII. */
export const FIXTURE_PRO_REFERRALS: MyReferralsResponse = {
  links: [],
  rewards: [
    {
      id: "reward-pro-earned",
      kind: "pro_crm_month",
      status: "earned",
      created_at: "2026-07-12T12:00:00Z",
    },
    {
      id: "reward-pro-fulfilled",
      kind: "pro_crm_month",
      status: "fulfilled",
      fulfilled_at: "2026-07-18T12:00:00Z",
      created_at: "2026-07-10T12:00:00Z",
    },
  ],
  waitlist_position: null,
};

// ─────────────────────────────────────────────────────────────────────────
// Clients — ordered loosely; the list applies its own "Needs attention" sort.
// ─────────────────────────────────────────────────────────────────────────

export const FIXTURE_CLIENTS: ClientVM[] = [
  // ── 1. Verified, full_financial, hot / needs response ───────────────
  {
    id: "11111111-1111-4111-8111-111111111111",
    // Keyed to FIXTURE_PEOPLE's Nina row so the PersonDetail preview can
    // resolve the connected enrichment (useClientDetail matches on contactId).
    contactId: "people-nina-contact",
    name: "Nina & Theo Park",
    email: "nina.park@example.com",
    phone: "(206) 555-0148",
    avatar: "NP",
    tier: 3,
    timeline: "Buying in 0–3 months",
    timelineShort: "0–3 mo",
    lastActivity: "18 minutes ago",
    minutesAgo: 18,
    changed: "Re-ran a Ballard listing at $710k after a bank refresh",
    newEvents: 3,
    activeThisWeek: true,
    changedThisWeek: true,
    needsResponse: true,
    area: "Ballard, Seattle",
    goal: "First home with room for a second kid; wants a quiet block near a park.",
    budget: "$680k–$720k",
    creditBand: "740–779",
    affordability: {
      qualPrice: "$725,000",
      band: "$680k–$720k",
      bandCompact: "$680–720k",
      movement: { direction: "up", amount: "$25k", asOf: "this week" },
      downPayment: "ready",
      targetBudget: "$700,000",
      pending: false,
      score: 84,
      segment: "ready_to_act",
      segmentLabel: "Ready to Act",
      target: "$700k",
    },
    financials: {
      income: {
        value: "$214,000 / yr",
        sub: "W-2 + RSU, 2 earners",
        verb: "Verified",
        date: "May 28, 2026",
        kind: "bank",
      },
      savings: {
        value: "$96,400",
        sub: "Across 2 linked accounts",
        verb: "Verified",
        date: "May 28, 2026",
        kind: "bank",
      },
      debts: {
        value: "$640 / mo",
        sub: "1 auto loan, no cards carried",
        verb: "Verified",
        date: "May 28, 2026",
        kind: "bank",
      },
    },
    underwriting: {
      creditScore: 0,
      creditPull: "Self-reported",
      dti: "34%",
      frontDti: "22%",
      reserves: "6.1 months",
      scenarios: [
        {
          label: "30-yr fixed",
          rate: "6.75%",
          payment: "$3,640 / mo",
          note: "Base case · $690k @ 10% down",
        },
        {
          label: "7/6 ARM",
          rate: "6.20%",
          payment: "$3,420 / mo",
          note: "If they plan to move < 7 yrs",
        },
        {
          label: "FHA 30-yr",
          rate: "6.40%",
          payment: "$3,560 / mo",
          note: "Lower down, adds MIP",
        },
      ],
    },
    activity: [
      {
        type: "listing_analysis_run",
        text: "Ran affordability on a new listing",
        address: "2417 NW 62nd St",
        price: "$710,000",
        time: "18 min ago",
      },
      {
        type: "verification_tier_changed",
        text: "Refreshed linked bank accounts — verification renewed",
        highlights: ["verification renewed"],
        time: "1 hr ago",
      },
      {
        type: "profile_updated",
        text: "Nudged target price up after the refresh",
        highlights: ["target price up"],
        time: "1 hr ago",
      },
    ],
    connection: {
      linkId: "link-nina",
      role: "lo",
      sharingLevel: "full_financial",
      method: "referral_link",
      funnelState: "linked",
      since: "Connected Mar 2026",
    },
    stage: "agreement",
    stageUpdatedAt: "2026-06-01T12:00:00Z",
    // SILL-379: newest-first so the expanded-row "Latest note:" snippet (which
    // takes notes[0]) has something to surface in preview mode.
    notes: [
      {
        id: "note-nina-1",
        body: "Wants a quiet block near a park; flagged the NW 62nd St listing as the front-runner and asked about a 7/6 ARM before locking.",
        author: "You",
        createdAt: "2 hours ago",
      },
      {
        id: "note-nina-2",
        body: "Pre-approved through the bank refresh; second kid on the way, so timeline is firm at 0–3 months.",
        author: "You",
        createdAt: "Yesterday",
      },
    ],
  },

  // ── 2. Verified, full_financial, steady momentum ────────────────────
  {
    id: "22222222-2222-4222-8222-222222222222",
    name: "Marcus Hale",
    email: "marcus.hale@example.com",
    phone: "(206) 555-0173",
    avatar: "MH",
    tier: 3,
    timeline: "Buying in 3–6 months",
    timelineShort: "3–6 mo",
    lastActivity: "2 hours ago",
    minutesAgo: 120,
    changed: "Compared 10% vs 15% down on a Fremont condo",
    newEvents: 1,
    activeThisWeek: true,
    changedThisWeek: true,
    needsResponse: false,
    area: "Fremont, Seattle",
    goal: "Trading a rental for a condo close to work; wants the monthly to stay flat.",
    budget: "$520k–$560k",
    creditBand: "780+",
    financials: {
      income: {
        value: "$162,000 / yr",
        sub: "Single earner, salaried",
        verb: "Verified",
        date: "May 30, 2026",
        kind: "bank",
      },
      savings: {
        value: "$74,800",
        sub: "Checking + brokerage",
        verb: "Verified",
        date: "May 30, 2026",
        kind: "bank",
      },
      debts: {
        value: "$310 / mo",
        sub: "Student loan only",
        verb: "Verified",
        date: "May 30, 2026",
        kind: "bank",
      },
    },
    underwriting: {
      creditScore: 0,
      creditPull: "Self-reported",
      dti: "28%",
      frontDti: "19%",
      reserves: "8.4 months",
      scenarios: [
        {
          label: "30-yr fixed",
          rate: "6.70%",
          payment: "$2,820 / mo",
          note: "Base case · $540k @ 15% down",
        },
        {
          label: "15-yr fixed",
          rate: "5.95%",
          payment: "$3,860 / mo",
          note: "Faster payoff, higher monthly",
        },
      ],
    },
    activity: [
      {
        type: "listing_analysis_run",
        text: "Compared down-payment scenarios",
        address: "3520 Fremont Ave N #4",
        price: "$549,000",
        time: "2 hr ago",
      },
      {
        type: "profile_updated",
        text: "Confirmed savings after a transfer",
        highlights: ["savings"],
        time: "Yesterday",
      },
    ],
    connection: {
      linkId: "link-marcus",
      role: "lo",
      sharingLevel: "full_financial",
      method: "marketplace",
      funnelState: "linked",
      since: "Connected Feb 2026",
    },
  },

  // ── 3. Document, full_financial, going cold ──────────────────────────
  {
    id: "33333333-3333-4333-8333-333333333333",
    name: "Priya Nair",
    email: "priya.nair@example.com",
    avatar: "PN",
    tier: 2,
    timeline: "Buying in 6–12 months",
    timelineShort: "6–12 mo",
    lastActivity: "9 days ago",
    minutesAgo: 12960,
    changed: "Uploaded two pay stubs and a 1099, then went quiet",
    newEvents: 0,
    activeThisWeek: false,
    changedThisWeek: false,
    needsResponse: true,
    area: "Green Lake, Seattle",
    goal: "Wants a small house with a yard; self-employed income makes her nervous.",
    budget: "$600k–$650k",
    creditBand: "700–739",
    financials: {
      income: {
        value: "$138,000 / yr",
        sub: "1099 + part-time W-2",
        verb: "Verified",
        date: "May 29, 2026",
        kind: "doc",
        docNote: "2 pay stubs + 1099",
      },
      savings: {
        value: "$58,000",
        sub: "Stated on application",
        verb: "Verified",
        date: "May 29, 2026",
        kind: "doc",
        docNote: "2 bank statements",
      },
      debts: {
        value: "$420 / mo",
        sub: "Card minimums",
        verb: "Self-reported",
        date: null,
        kind: "self",
      },
    },
    underwriting: {
      creditScore: 0,
      creditPull: "Self-reported",
      dti: "39%",
      frontDti: "27%",
      reserves: "4.2 months",
      scenarios: [
        {
          label: "30-yr fixed",
          rate: "6.85%",
          payment: "$3,240 / mo",
          note: "Base case · $620k @ 10% down",
        },
        {
          label: "FHA 30-yr",
          rate: "6.55%",
          payment: "$3,180 / mo",
          note: "Lower down for self-employed",
        },
      ],
    },
    activity: [
      {
        type: "profile_updated",
        text: "Uploaded income documents",
        highlights: ["income documents"],
        time: "9 days ago",
      },
      {
        type: "going_cold",
        text: "No activity for over a week",
        time: "Today",
      },
    ],
    connection: {
      linkId: "link-priya",
      role: "lo",
      sharingLevel: "full_financial",
      method: "concierge",
      funnelState: "linked",
      since: "Connected Jan 2026",
    },
  },

  // ── 4. Document, STATUS_ONLY — no magnitudes (the contract on screen) ─
  {
    id: "44444444-4444-4444-8444-444444444444",
    name: "Dani & Rob Whitfield",
    email: "dani.whitfield@example.com",
    avatar: "DW",
    tier: 2,
    timeline: "Buying in 3–6 months",
    timelineShort: "3–6 mo",
    lastActivity: "5 hours ago",
    minutesAgo: 300,
    changed: "Shared progress, but kept financials private",
    newEvents: 2,
    activeThisWeek: true,
    changedThisWeek: true,
    needsResponse: false,
    area: "Wallingford, Seattle",
    goal: "Upsizing for a home office; sharing status only until they pick an agent.",
    // status_only → no exact price; only the bucketed band is shown.
    budget: "$700k–$800k band",
    creditBand: "Not shared",
    financials: {
      income: {
        verb: "Not shared at this tier",
        date: null,
        kind: "self",
      },
      savings: {
        verb: "Not shared at this tier",
        date: null,
        kind: "self",
      },
      debts: {
        verb: "Not shared at this tier",
        date: null,
        kind: "self",
      },
    },
    underwriting: null,
    activity: [
      {
        type: "listing_analysis_run",
        text: "Ran a listing in their target area",
        // status_only activity carries area + band, NOT address/price.
        time: "5 hr ago",
      },
      {
        type: "lease_window_hit",
        text: "Entered their lease-end window",
        highlights: ["lease-end window"],
        time: "5 hr ago",
      },
    ],
    connection: {
      linkId: "link-dani",
      role: "agent",
      sharingLevel: "status_only",
      method: "referral_link",
      funnelState: "linked",
      since: "Connected Apr 2026",
    },
  },

  // ── 5. Self-reported, status_only, hidden by default ─────────────────────
  {
    id: "55555555-5555-4555-8555-555555555555",
    name: "Owen Castellano",
    email: "owen.c@example.com",
    avatar: "OC",
    tier: 1,
    timeline: "Buying in 6–12 months",
    timelineShort: "6–12 mo",
    lastActivity: "3 days ago",
    minutesAgo: 4320,
    changed: "Filled out a profile but hasn't linked a bank or uploaded docs",
    newEvents: 0,
    activeThisWeek: false,
    changedThisWeek: false,
    needsResponse: false,
    area: "Beacon Hill, Seattle",
    goal: "Early-stage; just exploring what's affordable on one income.",
    budget: "$450k–$500k band",
    creditBand: "Not shared",
    financials: {
      income: {
        sub: "Self-reported on profile",
        verb: "Self-reported",
        date: null,
        kind: "self",
      },
      savings: {
        sub: "Self-reported on profile",
        verb: "Self-reported",
        date: null,
        kind: "self",
      },
      debts: {
        sub: "Self-reported on profile",
        verb: "Self-reported",
        date: null,
        kind: "self",
      },
    },
    underwriting: null,
    activity: [
      {
        type: "profile_updated",
        text: "Updated their buying timeline",
        highlights: ["timeline"],
        time: "3 days ago",
      },
    ],
    connection: {
      linkId: "link-owen",
      role: "agent",
      sharingLevel: "status_only",
      method: "manual_code",
      funnelState: "signed_up",
      since: "Connected May 2026",
    },
  },

  // ── 6. Self-reported, status_only, opened invite (early funnel) ──────────
  {
    id: "66666666-6666-4666-8666-666666666666",
    name: "Bea Lindqvist",
    email: "bea.l@example.com",
    avatar: "BL",
    tier: 1,
    timeline: "Buying in 12+ months",
    timelineShort: "12+ mo",
    lastActivity: "6 days ago",
    minutesAgo: 8640,
    changed: "Opened the invite, browsed once, hasn't finished a profile",
    newEvents: 0,
    activeThisWeek: false,
    changedThisWeek: false,
    needsResponse: false,
    area: "Phinney Ridge, Seattle",
    goal: "Just opened the invite — no profile yet.",
    budget: "Not shared",
    creditBand: "Not shared",
    financials: {
      income: { verb: "Self-reported", date: null, kind: "self" },
      savings: { verb: "Self-reported", date: null, kind: "self" },
      debts: { verb: "Self-reported", date: null, kind: "self" },
    },
    underwriting: null,
    activity: [
      {
        type: "invite_accepted",
        text: "Opened your invite",
        time: "6 days ago",
      },
    ],
    connection: {
      linkId: "link-bea",
      role: "agent",
      sharingLevel: "status_only",
      method: "referral_link",
      funnelState: "opened",
      since: "Invited May 2026",
    },
  },

  // ── 7. Verified, full_financial, agent-role, rate-watch nudge ───────
  {
    id: "77777777-7777-4777-8777-777777777777",
    name: "Sofia & Liang Chen",
    email: "sofia.chen@example.com",
    avatar: "SC",
    tier: 3,
    timeline: "Buying in 0–3 months",
    timelineShort: "0–3 mo",
    lastActivity: "40 minutes ago",
    minutesAgo: 40,
    changed: "Rates dipped into their range; budget headroom opened up",
    newEvents: 2,
    activeThisWeek: true,
    changedThisWeek: true,
    needsResponse: true,
    area: "Magnolia, Seattle",
    goal: "Ready to move on the right place; very rate-sensitive.",
    budget: "$820k–$880k",
    creditBand: "780+",
    financials: {
      income: {
        value: "$268,000 / yr",
        sub: "2 earners, 1 bonus-heavy",
        verb: "Verified",
        date: "Jun 1, 2026",
        kind: "bank",
      },
      savings: {
        value: "$158,000",
        sub: "3 linked accounts",
        verb: "Verified",
        date: "Jun 1, 2026",
        kind: "bank",
      },
      debts: {
        value: "$1,120 / mo",
        sub: "Auto + small card balance",
        verb: "Verified",
        date: "Jun 1, 2026",
        kind: "bank",
      },
    },
    underwriting: {
      creditScore: 0,
      creditPull: "Self-reported",
      dti: "31%",
      frontDti: "24%",
      reserves: "9.0 months",
      scenarios: [
        {
          label: "30-yr fixed",
          rate: "6.60%",
          payment: "$4,510 / mo",
          note: "Base case · $850k @ 20% down",
        },
        {
          label: "7/6 ARM",
          rate: "6.05%",
          payment: "$4,210 / mo",
          note: "Strong reserves favor an ARM",
        },
      ],
    },
    activity: [
      {
        type: "rate_watch_shift",
        text: "Rates moved into their target range",
        highlights: ["into their target range"],
        time: "40 min ago",
      },
      {
        type: "listing_analysis_run",
        text: "Ran a Magnolia listing at the new rate",
        address: "3110 W Howe St",
        price: "$869,000",
        time: "35 min ago",
      },
    ],
    connection: {
      linkId: "link-sofia",
      role: "agent",
      sharingLevel: "full_financial",
      method: "marketplace",
      funnelState: "linked",
      since: "Connected Feb 2026",
    },
  },

  // ── 8. Document, full_financial, band-up momentum ────────────────────
  {
    id: "88888888-8888-4888-8888-888888888888",
    name: "Trevor Okafor",
    email: "trevor.okafor@example.com",
    avatar: "TO",
    tier: 2,
    timeline: "Buying in 3–6 months",
    timelineShort: "3–6 mo",
    lastActivity: "Yesterday",
    minutesAgo: 1500,
    changed: "Crossed into a stronger affordability band after a raise",
    newEvents: 1,
    activeThisWeek: true,
    changedThisWeek: true,
    needsResponse: false,
    area: "Columbia City, Seattle",
    goal: "Wants a townhouse near the light rail; just got a raise.",
    budget: "$560k–$610k",
    creditBand: "720–759",
    financials: {
      income: {
        value: "$149,500 / yr",
        sub: "Salaried, new role",
        verb: "Verified",
        date: "May 27, 2026",
        kind: "doc",
        docNote: "Offer letter + 1 pay stub",
      },
      savings: {
        value: "$61,200",
        sub: "Checking + HYSA",
        verb: "Verified",
        date: "May 27, 2026",
        kind: "doc",
        docNote: "2 bank statements",
      },
      debts: {
        value: "$510 / mo",
        sub: "Auto loan",
        verb: "Self-reported",
        date: null,
        kind: "self",
      },
    },
    underwriting: {
      creditScore: 0,
      creditPull: "Self-reported",
      dti: "33%",
      frontDti: "23%",
      reserves: "5.0 months",
      scenarios: [
        {
          label: "30-yr fixed",
          rate: "6.78%",
          payment: "$3,010 / mo",
          note: "Base case · $585k @ 10% down",
        },
      ],
    },
    activity: [
      {
        type: "band_up",
        text: "Moved up an affordability band",
        highlights: ["up an affordability band"],
        time: "Yesterday",
      },
      {
        type: "profile_updated",
        text: "Updated income after a raise",
        highlights: ["income"],
        time: "2 days ago",
      },
    ],
    connection: {
      linkId: "link-trevor",
      role: "lo",
      sharingLevel: "full_financial",
      method: "referral_link",
      funnelState: "linked",
      since: "Connected Mar 2026",
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────
// Right-rail content.
// ─────────────────────────────────────────────────────────────────────────

export const FIXTURE_SUGGESTION: SillSuggestion = {
  clientId: "11111111-1111-4111-8111-111111111111",
  text: "Nina & Theo refreshed their bank and re-ran a Ballard listing in the last hour. They're verified and buying in 0–3 months — a good moment to reach out before they tour.",
  highlights: ["refreshed their bank", "buying in 0–3 months"],
};

export const FIXTURE_SIGNALS: SignalEvent[] = [
  {
    clientId: "77777777-7777-4777-8777-777777777777",
    who: "Sofia & Liang Chen",
    tier: 3,
    text: "Rates moved into their target range and they re-ran a Magnolia listing.",
    highlights: ["into their target range"],
    time: "40 min ago",
    tone: "hot",
  },
  {
    clientId: "11111111-1111-4111-8111-111111111111",
    who: "Nina & Theo Park",
    tier: 3,
    text: "Refreshed linked accounts, then ran a $710k Ballard listing.",
    highlights: ["$710k Ballard listing"],
    time: "18 min ago",
    tone: "good",
  },
  {
    clientId: "88888888-8888-4888-8888-888888888888",
    who: "Trevor Okafor",
    tier: 2,
    text: "Crossed up an affordability band after a raise.",
    highlights: ["up an affordability band"],
    time: "Yesterday",
    tone: "good",
  },
  {
    clientId: "44444444-4444-4444-8444-444444444444",
    who: "Dani & Rob Whitfield",
    tier: 2,
    text: "Entered their lease-end window — timing is getting real.",
    highlights: ["lease-end window"],
    time: "5 hr ago",
    tone: "default",
  },
  {
    clientId: "33333333-3333-4333-8333-333333333333",
    who: "Priya Nair",
    tier: 2,
    text: "Quiet for 9 days after uploading documents.",
    highlights: ["Quiet for 9 days"],
    time: "Today",
    tone: "hot",
  },
];

// ─────────────────────────────────────────────────────────────────────────
// KPI strip (design §4.4). Derived from the fixture book, hardcoded for clarity.
// ─────────────────────────────────────────────────────────────────────────

export const FIXTURE_KPIS: KpiMetric[] = [
  { label: "Active this week", value: "5", tone: "default" },
  { label: "Changed this week", value: "5", tone: "bank" },
  { label: "Needs response", value: "3", tone: "warn" },
  { label: "Going cold", value: "2", tone: "default" },
  {
    label: "Avg. response",
    value: "2.4h",
    delta: "↓ 0.6h",
    tone: "default",
  },
];

// ─────────────────────────────────────────────────────────────────────────
// Contacts — the "book of business". A realistic MIXED book: linked clients
// (the same people who appear in FIXTURE_CLIENTS) plus un-invited leads and a
// few mid-funnel contacts. PRIVACY: an "added" contact is just "in your book,
// no invite sent" — nothing here implies they're a Sill member. Only a
// "linked" contact (consumer consented) carries linkedProfileId.
// ─────────────────────────────────────────────────────────────────────────

/** Preview People data exercises base-only, connected, and partial enrichment rows. */
export const FIXTURE_PEOPLE: PersonVM[] = [
  {
    contactId: "people-nina-contact",
    name: "Nina & Theo Park",
    avatar: "NP",
    verificationTier: 3,
    email: "nina.park@example.com",
    area: "Ballard, Seattle",
    tags: ["Referral", "Family"],
    relationshipStage: "active",
    stageUpdatedAt: "2026-08-04T12:00:00Z",
    sillStatus: "connected",
    timeline: "Buying in 0–3 months",
    timelineShort: "0–3 mo",
    engagement: {
      attention: 1,
      nextTaskDueAt: "2026-08-06T12:00:00Z",
      latestActivityAt: "2026-08-05T08:00:00Z",
    },
    affordability: {
      band: "$680k–$720k",
      verificationLabel: "Verified",
      movement: { direction: "up", amount: "$25k", asOf: "this week" },
    },
    enrichmentState: "available",
    capabilities: {
      canInvite: false,
      canEmail: true,
      canSchedule: true,
      canTask: true,
      canNote: true,
      canInAppMessage: true,
    },
  },
  {
    contactId: "people-ada-contact",
    name: "Ada Lovelace",
    avatar: "AL",
    verificationTier: 1,
    email: "ada@example.com",
    area: "Seattle",
    tags: ["Open house"],
    relationshipStage: "connected",
    sillStatus: "not_invited",
    timeline: "Buying in 3–6 months",
    timelineShort: "3–6 mo",
    engagement: { attention: 2, latestNoteAt: "2026-08-04T10:00:00Z" },
    enrichmentState: "not_connected",
    capabilities: {
      canInvite: true,
      canEmail: true,
      canSchedule: true,
      canTask: true,
      canNote: true,
      canInAppMessage: false,
    },
  },
  {
    contactId: "people-sam-contact",
    name: "Sam Rivera",
    avatar: "SR",
    // Connected + unavailable enrichment ⇒ tier UNKNOWN on the real wire
    // (adaptPersonRow leaves it undefined) — the roster renders no tier claim.
    phone: "(206) 555-0188",
    area: "Fremont, Seattle",
    tags: [],
    relationshipStage: "agreement",
    sillStatus: "connected",
    timeline: "Buying in 6–12 months",
    timelineShort: "6–12 mo",
    engagement: {
      attention: 3,
      lastOutboundAt: "2026-08-02T12:00:00Z",
      lastOutboundStatus: "sent",
    },
    enrichmentState: "unavailable",
    capabilities: {
      canInvite: false,
      canEmail: true,
      canSchedule: true,
      canTask: true,
      canNote: true,
      // Mirrors crm/capability.go: connected + not archived ⇒ allowed —
      // messaging never depends on enrichment being readable.
      canInAppMessage: true,
    },
  },
];

/** Server-shaped quick-view totals used by the People preview. */
export const FIXTURE_PEOPLE_COUNTS = {
  all: FIXTURE_PEOPLE.length,
  active: 2,
  needs_me: 1,
  ready_soon: 1,
  going_cold: 0,
  archived: 0,
};

export const FIXTURE_CONTACTS: ContactVM[] = [
  {
    id: "c-nina",
    name: "Nina Park",
    avatar: "NP",
    email: "nina.park@example.com",
    phone: "(206) 555-0148",
    firstName: "Nina",
    lastName: "Park",
    notes: "Met at the Ballard open house. Pre-approved, very motivated.",
    tags: ["ballard", "0-3mo"],
    source: "referral",
    funnelState: "linked",
    linkedProfileId: "11111111-1111-4111-8111-111111111111",
    added: "Added Mar 2026",
    area: "Ballard",
    horizon: "0–3 months",
    archivedAt: null,
    stage: "agreement",
    stageUpdatedAt: "2026-06-01T12:00:00Z",
  },
  {
    id: "c-marcus",
    name: "Marcus Hale",
    avatar: "MH",
    email: "marcus.hale@example.com",
    firstName: "Marcus",
    lastName: "Hale",
    notes: "Trading a rental for a condo near work.",
    tags: ["fremont", "condo"],
    source: "marketplace",
    funnelState: "linked",
    linkedProfileId: "22222222-2222-4222-8222-222222222222",
    added: "Added Feb 2026",
    area: "Fremont",
    horizon: "3–6 months",
    archivedAt: null,
    stage: "active",
    stageUpdatedAt: "2026-06-10T09:30:00Z",
  },
  {
    id: "c-owen",
    name: "Owen Castellano",
    avatar: "OC",
    email: "owen.c@example.com",
    firstName: "Owen",
    lastName: "Castellano",
    notes: "Signed up but hasn't linked a bank yet. Nudge in a week.",
    tags: ["beacon-hill"],
    source: "manual",
    funnelState: "signed_up",
    added: "Added May 2026",
    area: "Beacon Hill",
    horizon: "",
    archivedAt: null,
    stage: "connected",
    stageUpdatedAt: "2026-05-01T08:00:00Z",
  },
  {
    id: "c-bea",
    name: "Bea Lindqvist",
    avatar: "BL",
    email: "bea.l@example.com",
    firstName: "Bea",
    lastName: "Lindqvist",
    notes: "Opened the invite, browsed once. Long timeline.",
    tags: ["phinney"],
    source: "referral",
    funnelState: "opened",
    added: "Added May 2026",
    area: "Phinney Ridge",
    horizon: "12+ months",
    archivedAt: null,
    stage: "connected",
    stageUpdatedAt: "2026-05-01T08:00:00Z",
  },
  {
    id: "c-james",
    name: "James Whitlock",
    avatar: "JW",
    email: "james.whitlock@example.com",
    phone: "(206) 555-0199",
    firstName: "James",
    lastName: "Whitlock",
    notes: "Invited after the spring open house. No reply yet.",
    tags: ["queen-anne"],
    source: "csv_import",
    funnelState: "invited",
    added: "Added Apr 2026",
    area: "Queen Anne",
    horizon: "6–12 months",
    archivedAt: null,
    stage: "connected",
    stageUpdatedAt: "2026-04-01T08:00:00Z",
  },
  {
    id: "c-rosa",
    name: "Rosa Delgado",
    avatar: "RD",
    email: "rosa.delgado@example.com",
    firstName: "Rosa",
    lastName: "Delgado",
    notes: "Past client's referral. Haven't reached out yet.",
    tags: ["referral", "warm"],
    source: "referral",
    funnelState: "added",
    added: "Added May 2026",
    area: "",
    horizon: "",
    archivedAt: null,
    stage: "connected",
    stageUpdatedAt: "2026-05-01T08:00:00Z",
  },
  {
    id: "c-kev",
    name: "Kevin Tran",
    avatar: "KT",
    email: "kevin.tran@example.com",
    phone: "(206) 555-0162",
    firstName: "Kevin",
    lastName: "Tran",
    notes: "From the brokerage lead sheet. Cold — needs an intro.",
    tags: ["lead-sheet"],
    source: "csv_import",
    funnelState: "added",
    added: "Added Apr 2026",
    area: "",
    horizon: "",
    archivedAt: null,
    stage: "connected",
    stageUpdatedAt: "2026-04-01T08:00:00Z",
  },
  {
    id: "c-amelia",
    name: "Amelia Ford",
    avatar: "AF",
    email: "amelia.ford@example.com",
    firstName: "Amelia",
    lastName: "Ford",
    notes: "Met at a listing. Wants to start in the fall.",
    tags: ["fall-2026"],
    source: "manual",
    funnelState: "added",
    added: "Added May 2026",
    area: "",
    horizon: "6–12 months",
    archivedAt: null,
    stage: "connected",
    stageUpdatedAt: "2026-05-01T08:00:00Z",
  },
  // Contact without an email address — exercises the "No email on file"
  // disabled-row variant in the ContactGroup (DB scenario 92).
  {
    id: "c-no-email",
    name: "Sam Chen",
    avatar: "SC",
    firstName: "Sam",
    lastName: "Chen",
    notes: "Business card only — no email collected yet.",
    tags: ["pending"],
    source: "manual",
    funnelState: "added",
    added: "Added Jun 2026",
    area: "",
    horizon: "",
    stage: "connected",
    stageUpdatedAt: "2026-06-01T08:00:00Z",
  },
];

export const FIXTURE_CONTACT_FUNNEL: ContactFunnelSummaryVM = {
  added: 4,
  invited: 1,
  opened: 1,
  signed_up: 1,
  linked: 2,
};

// A sample CSV import preview the mapping-confirm step renders against. The
// suggested mapping is the heuristic the backend would return for these
// headers (email→2, first_name→0, last_name→1, phone→3).
export const FIXTURE_IMPORT_PREVIEW: ImportPreviewVM = {
  batchId: "batch-preview-fixture",
  filename: "spring-leads.csv",
  headers: ["First Name", "Last Name", "Email", "Phone", "Notes"],
  suggestedMapping: { first_name: 0, last_name: 1, email: 2, phone: 3 },
  previewRows: [
    [
      "Dana",
      "Reyes",
      "dana.reyes@example.com",
      "(206) 555-0110",
      "Zillow lead",
    ],
    [
      "Marcus",
      "Webb",
      "marcus.webb@example.com",
      "(206) 555-0121",
      "Open house",
    ],
    ["Priya", "Shah", "priya.shah@example.com", "", "Referral from Sam"],
    ["Leo", "Nakamura", "leo.n@example.com", "(206) 555-0143", ""],
    ["", "", "noname@example.com", "(206) 555-0150", "Missing name"],
  ],
  totalRows: 42,
};

// ─────────────────────────────────────────────────────────────────────────
// Cross-client activity feed (the /activity surface). Chronological; spans
// hot / good / default tones and all three tiers. status_only rows (Dani)
// carry NO magnitude — only a label/area, mirroring the projector.
// ─────────────────────────────────────────────────────────────────────────

export const FIXTURE_ACTIVITY_FEED: ActivityFeedItem[] = [
  {
    id: "f-1",
    type: "rate_watch_shift",
    who: "Sofia & Liang Chen",
    tier: 3,
    text: "Rates moved into their target range",
    highlights: ["into their target range"],
    occurredAt: "2026-06-02T13:20:00Z",
    time: "40 min ago",
    tone: "hot",
    profileId: "77777777-7777-4777-8777-777777777777",
  },
  {
    id: "f-2",
    type: "listing_analysis_run",
    who: "Sofia & Liang Chen",
    tier: 3,
    text: "Ran a Magnolia listing at the new rate",
    magnitude: "$869,000",
    occurredAt: "2026-06-02T13:25:00Z",
    time: "35 min ago",
    tone: "default",
    profileId: "77777777-7777-4777-8777-777777777777",
  },
  {
    id: "f-3",
    type: "listing_analysis_run",
    who: "Nina & Theo Park",
    tier: 3,
    text: "Ran affordability on a new Ballard listing",
    magnitude: "$710,000",
    occurredAt: "2026-06-02T13:42:00Z",
    time: "18 min ago",
    tone: "good",
    profileId: "11111111-1111-4111-8111-111111111111",
  },
  {
    id: "f-4",
    type: "verification_tier_changed",
    who: "Nina & Theo Park",
    tier: 3,
    text: "Refreshed linked accounts — verification renewed",
    highlights: ["verification renewed"],
    occurredAt: "2026-06-02T13:00:00Z",
    time: "1 hr ago",
    tone: "good",
    profileId: "11111111-1111-4111-8111-111111111111",
  },
  {
    id: "f-5",
    type: "lease_window_hit",
    who: "Dani & Rob Whitfield",
    tier: 2,
    // status_only → area/label only, no magnitude.
    text: "Entered their lease-end window in Wallingford",
    highlights: ["lease-end window"],
    occurredAt: "2026-06-02T09:00:00Z",
    time: "5 hr ago",
    tone: "hot",
    profileId: "44444444-4444-4444-8444-444444444444",
  },
  {
    id: "f-6",
    type: "band_up",
    who: "Trevor Okafor",
    tier: 2,
    text: "Crossed up an affordability band after a raise",
    highlights: ["up an affordability band"],
    occurredAt: "2026-06-01T16:30:00Z",
    time: "Yesterday",
    tone: "good",
    profileId: "88888888-8888-4888-8888-888888888888",
  },
  {
    id: "f-7",
    type: "going_cold",
    who: "Priya Nair",
    tier: 2,
    text: "Quiet for 9 days after uploading documents",
    highlights: ["Quiet for 9 days"],
    occurredAt: "2026-05-31T11:00:00Z",
    time: "2 days ago",
    tone: "hot",
    profileId: "33333333-3333-4333-8333-333333333333",
  },
  {
    id: "f-8",
    type: "invite_accepted",
    who: "Bea Lindqvist",
    tier: 1,
    text: "Opened your invite",
    occurredAt: "2026-05-27T10:00:00Z",
    time: "6 days ago",
    tone: "good",
    profileId: "66666666-6666-4666-8666-666666666666",
  },
];

// ─────────────────────────────────────────────────────────────────────────
// Pro profile + founding (the /profile surface). Matches FIXTURE_PRO (Jess
// Soto, LO). Founding progress is deterministic counters toward the goal.
// ─────────────────────────────────────────────────────────────────────────

export const FIXTURE_PRO_PROFILE: ProProfileVM = {
  role: "lo",
  name: "Jess Soto",
  avatar: "JS",
  brokerage: "Windermere · Ballard",
  licenseLabel: "NMLS #",
  licenseValue: "1845207",
  serviceAreas: ["Ballard", "Fremont", "Green Lake", "Magnolia"],
  bio: "Seattle loan officer focused on first-time buyers. I keep the math honest and the timeline realistic — no surprises at the closing table.",
  directoryPublic: true,
  inviteCode: "JESS-SOTO-PRO",
  bookingLink: "",
  founding: {
    member: true,
    programSlug: "founding-agents",
    invitesSent: 24,
    clientsLinked: 6,
    clientsVerified: 4,
    goal: 10,
  },
};

// ─────────────────────────────────────────────────────────────────────────
// Notification preferences (the /settings surface). Built from the shared
// catalog so the preview matches the prod overlay shape exactly; a couple are
// flipped off to exercise both switch states.
// ─────────────────────────────────────────────────────────────────────────

export const FIXTURE_NOTIFICATION_PREFS: NotificationPrefVM[] =
  NOTIFICATION_CATALOG.map((entry) => ({
    ...entry,
    frequency: entry.defaultOn ? entry.onFrequency : "off",
  }));

// ─────────────────────────────────────────────────────────────────────────
// Marketplace payments (M6-A) — the pro Settings Billing section. No-card is
// the more representative preview default (a fresh pro hasn't added a card
// yet); a card-on-file fixture would hide the empty-state affordances.
// ─────────────────────────────────────────────────────────────────────────

export const FIXTURE_BILLING_STATUS: BillingStatusVM = {
  hasCard: false,
  brand: "",
  last4: "",
  expMonth: 0,
  expYear: 0,
  isExpired: false,
};

// Variants for the two card-present BillingSection states (mirrors
// BillingSection.test.tsx's `fe-card-summary`/`fe-card-warning` unit
// fixtures). `useBilling()` serves these under IS_PREVIEW when the e2e
// query-param seam requests them (`?billing=card` / `?billing=expired` —
// see `previewQueryParam` in lib/pro-mode.ts and useBilling() in
// hooks/useProData.ts). See frontend/pro/e2e/marketplace-billing.spec.ts
// for the e2e states these back.
export const FIXTURE_BILLING_STATUS_CARD_ON_FILE: BillingStatusVM = {
  hasCard: true,
  brand: "visa",
  last4: "4242",
  expMonth: 12,
  expYear: 2030,
  isExpired: false,
};

export const FIXTURE_BILLING_STATUS_EXPIRED: BillingStatusVM = {
  hasCard: true,
  brand: "visa",
  last4: "4242",
  expMonth: 1,
  expYear: 2020,
  isExpired: true,
};

// ─────────────────────────────────────────────────────────────────────────
// Sill Pro CRM subscription (the Settings "Sill Pro subscription" section +
// the /welcome onboarding pricing panel). One shared catalog + four plan-state
// variants, selected via `?crmbilling=free|pro|past_due|canceled`
// (previewQueryParam, see useCRMBilling() in hooks/useProData.ts). "free" is
// the default (a fresh pro hasn't subscribed yet).
// ─────────────────────────────────────────────────────────────────────────

const FIXTURE_CRM_BILLING_CATALOG = {
  monthlyCents: 4900,
  annualCents: 49000,
  // Deliberately the GLOBAL fallback figures — distinct from
  // FIXTURE_CRM_BILLING_OFFER below, so a regression that silently falls
  // back to reading the catalog instead of the per-account offer renders a
  // visibly wrong 50%/3 in preview instead of coincidentally matching.
  foundingDiscountPercent: 50,
  foundingDiscountMonths: 3,
};

// The account's PER-ACCOUNT resolved offer (spec D-OFFER-3) — a real
// acquisition-campaign figure, deliberately NOT 50/12 (nor matching the
// catalog above), so `?crmbilling=`/`?pricing=`/`?discount=` previews
// exercise genuinely per-account rendering, not the global catalog row.
const FIXTURE_CRM_BILLING_OFFER = {
  discountPercent: 25,
  discountMonths: 6,
  freeMonths: 1,
};

// The RESOLVED money picture the backend ships alongside the inputs above
// (`crmbilling.ResolveActivationPricing`). Written out literally rather than
// derived from the offer/catalog, because deriving it here would be the
// client-side pricing math this block exists to delete — a fixture stands in
// for a server response, so it states what the server would say.
//
// 25% off 4900 = 3675/mo and 25% off 49000 = 36750/yr, for the 6 months the
// offer's window is open.
const FIXTURE_CRM_BILLING_ACTIVATION = {
  discountActive: true,
  discountPercent: FIXTURE_CRM_BILLING_OFFER.discountPercent,
  discountMonths: FIXTURE_CRM_BILLING_OFFER.discountMonths,
  effectiveMonthlyCents: 3675,
  effectiveAnnualCents: 36750,
  annualDueTodayCents: 36750,
  // No starter promise on a subscribed/lapsed account.
  starterCoversMonths: 0,
  starterHasRemainder: false,
};

// The free-account variant, which DOES carry a starter promise: a 4900 credit
// against a 3675 invoice covers one whole month with 1225 left over — hence
// the "plus part of the next" tail in the /welcome preview.
const FIXTURE_CRM_BILLING_ACTIVATION_WITH_STARTER = {
  ...FIXTURE_CRM_BILLING_ACTIVATION,
  annualDueTodayCents: 36750 - 4900,
  starterCoversMonths: 1,
  starterHasRemainder: true,
};

export const FIXTURE_CRM_BILLING_FREE: CRMBillingSummaryVM = {
  plan: "free",
  status: "",
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  maxClients: 25,
  ai: false,
  aiDailyCap: 0,
  catalog: FIXTURE_CRM_BILLING_CATALOG,
  offer: FIXTURE_CRM_BILLING_OFFER,
  // Full-month starter promise — preserves the historical "first month on
  // us" /welcome preview by default. See starterActivationCopy (F6).
  starterPromisedCents: FIXTURE_CRM_BILLING_CATALOG.monthlyCents,
  // Full window still open — matches FIXTURE_PRO_PROFILE.founding.member.
  foundingDiscountMonthsRemaining: FIXTURE_CRM_BILLING_OFFER.discountMonths,
  activation: FIXTURE_CRM_BILLING_ACTIVATION_WITH_STARTER,
};

export const FIXTURE_CRM_BILLING_PRO: CRMBillingSummaryVM = {
  plan: "sill_pro_monthly",
  status: "active",
  currentPeriodEnd: "2026-12-31T00:00:00Z",
  cancelAtPeriodEnd: false,
  maxClients: -1,
  ai: true,
  aiDailyCap: 50,
  catalog: FIXTURE_CRM_BILLING_CATALOG,
  offer: FIXTURE_CRM_BILLING_OFFER,
  starterPromisedCents: 0,
  foundingDiscountMonthsRemaining: FIXTURE_CRM_BILLING_OFFER.discountMonths,
  activation: FIXTURE_CRM_BILLING_ACTIVATION,
};

export const FIXTURE_CRM_BILLING_PAST_DUE: CRMBillingSummaryVM = {
  plan: "sill_pro_monthly",
  status: "past_due",
  currentPeriodEnd: "2026-12-31T00:00:00Z",
  cancelAtPeriodEnd: false,
  maxClients: -1,
  ai: true,
  aiDailyCap: 50,
  catalog: FIXTURE_CRM_BILLING_CATALOG,
  offer: FIXTURE_CRM_BILLING_OFFER,
  starterPromisedCents: 0,
  foundingDiscountMonthsRemaining: FIXTURE_CRM_BILLING_OFFER.discountMonths,
  activation: FIXTURE_CRM_BILLING_ACTIVATION,
};

export const FIXTURE_CRM_BILLING_CANCEL_AT_PERIOD_END: CRMBillingSummaryVM = {
  plan: "sill_pro_monthly",
  status: "active",
  currentPeriodEnd: "2026-12-31T00:00:00Z",
  cancelAtPeriodEnd: true,
  maxClients: -1,
  ai: true,
  aiDailyCap: 50,
  catalog: FIXTURE_CRM_BILLING_CATALOG,
  offer: FIXTURE_CRM_BILLING_OFFER,
  starterPromisedCents: 0,
  foundingDiscountMonthsRemaining: FIXTURE_CRM_BILLING_OFFER.discountMonths,
  activation: FIXTURE_CRM_BILLING_ACTIVATION,
};

export const FIXTURE_CRM_BILLING_CANCELED: CRMBillingSummaryVM = {
  plan: "sill_pro_monthly",
  status: "canceled",
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  maxClients: 25,
  ai: false,
  aiDailyCap: 0,
  catalog: FIXTURE_CRM_BILLING_CATALOG,
  offer: FIXTURE_CRM_BILLING_OFFER,
  starterPromisedCents: 0,
  foundingDiscountMonthsRemaining: FIXTURE_CRM_BILLING_OFFER.discountMonths,
  activation: FIXTURE_CRM_BILLING_ACTIVATION,
};

// ─────────────────────────────────────────────────────────────────────────
// Assistant (copilot) daily allowance (SILL-521 M2 cluster 4) — the free-tier
// remaining-turns line + post-exhaustion composer swap in ChatDockPanel.
// Selected via `?allowance=free|free_exhausted|pro` (useAssistantAllowance()
// in hooks/useProData.ts). Absent/unrecognized falls back to "free".
// ─────────────────────────────────────────────────────────────────────────

export const FIXTURE_ASSISTANT_ALLOWANCE_FREE: AssistantAllowanceVM = {
  limit: 3,
  used: 1,
  remaining: 2,
  tier: "free",
};

export const FIXTURE_ASSISTANT_ALLOWANCE_FREE_EXHAUSTED: AssistantAllowanceVM =
  {
    limit: 3,
    used: 3,
    remaining: 0,
    tier: "free",
  };

export const FIXTURE_ASSISTANT_ALLOWANCE_PRO: AssistantAllowanceVM = {
  limit: 50,
  used: 6,
  remaining: 44,
  tier: "pro",
};

export const FIXTURE_RATE_CARD: RateCardRowVM[] = [
  {
    cause: "marketplace",
    tier: "t1",
    listCents: 5000,
    discountCents: 0,
    netCents: 5000,
  },
  {
    cause: "marketplace",
    tier: "t2",
    listCents: 10000,
    discountCents: 0,
    netCents: 10000,
  },
  {
    cause: "marketplace",
    tier: "t3",
    listCents: 15000,
    discountCents: 0,
    netCents: 15000,
  },
  {
    cause: "marketplace",
    tier: "t4",
    listCents: 25000,
    discountCents: 0,
    netCents: 25000,
  },
];

// ─────────────────────────────────────────────────────────────────────────
// Bell + notification log (SILL-414). Mirrors what the enriched automation
// notifier adapter produces: realtime automation_notify rows with human,
// per-trigger copy, a client_profile tap-route (target ids match
// FIXTURE_CLIENTS so the preview routes land), one already-read row (proving
// the log is not unread-only), and one non-tappable row (unresolved client).
// ─────────────────────────────────────────────────────────────────────────

const minutesAgo = (m: number) =>
  new Date(Date.now() - m * 60_000).toISOString();

export const FIXTURE_NOTIFICATIONS: NotificationItem[] = [
  {
    id: "notif-draft-preview",
    type: "draft_waiting",
    urgency: "digest",
    state_class: "action_required",
    content: {
      type: "text",
      title: "A draft is ready",
      body: "Review the prepared follow-up before it is sent.",
    },
    action: { kind: "draft", target_id: "draft-preview" },
    read_state: "unread",
    created_at: minutesAgo(2),
  },
  {
    id: "notif-1",
    type: "automation_notify",
    urgency: "realtime",
    state_class: "info",
    content: {
      type: "text",
      title: "New document from Nina & Theo Park",
      body: 'From your "Doc follow-up" automation.',
    },
    action: {
      kind: "client_profile",
      target_id: "11111111-1111-4111-8111-111111111111",
    },
    read_state: "unread",
    created_at: minutesAgo(3),
  },
  {
    id: "notif-2",
    type: "automation_notify",
    urgency: "realtime",
    state_class: "info",
    content: {
      type: "text",
      title: "Marcus Hale accepted your invite",
      body: 'From your "New buyer welcome" automation.',
    },
    action: {
      kind: "client_profile",
      target_id: "22222222-2222-4222-8222-222222222222",
    },
    read_state: "unread",
    created_at: minutesAgo(42),
  },
  {
    id: "notif-3",
    type: "automation_notify",
    urgency: "realtime",
    state_class: "info",
    content: {
      type: "text",
      title: "Priya Nair connected their bank",
      body: 'From your "Onboarding nudge" automation.',
    },
    action: {
      kind: "client_profile",
      target_id: "33333333-3333-4333-8333-333333333333",
    },
    read_state: "read",
    created_at: minutesAgo(26 * 60),
  },
  {
    id: "notif-4",
    type: "automation_notify",
    urgency: "realtime",
    state_class: "info",
    content: {
      type: "text",
      title: "Automation update",
      body: "Triggered by one of your automations.",
    },
    action: null, // unresolved/unlinked client → non-tappable
    read_state: "unread",
    created_at: minutesAgo(5 * 60),
  },
];

// ─────────────────────────────────────────────────────────────────────────
// Thread panel (M8-A0, plan 289, task pro-thread-panel) — one seeded
// conversation on Nina & Theo Park (the hot, verified, linked client) so the
// ClientDetailView chat panel is screenshot-able under IS_PREVIEW without a
// backend or Clerk session. Kept keyed by client id so an unlinked/no-thread
// client still renders the panel's empty state (fixtures map miss → []).
// ─────────────────────────────────────────────────────────────────────────

/**
 * M11-B1 report reasons as the SERVER serves them to a pro
 * (GET /api/marketplace/report-reasons, `safety.proToBuyerReasons`).
 *
 * A preview fixture rather than a second source of truth: IS_PREVIEW runs with
 * no backend, so without this the report dialog would hit its fail-closed
 * branch and the flow would be unpreviewable. Values and copy must track the Go
 * list — pinned server-side by
 * TestGetMarketplaceReportReasons_PerDirection.
 *
 * Note there is no "not a real buyer": it was removed (operator decision,
 * 2026-07) because a buyer-accused report can only end in nothing, an internal
 * note, or marketplace removal — none of which is a sane outcome for a lead that
 * simply was not ready.
 */
export const FIXTURE_REPORT_REASONS: Array<{
  value: string;
  label: string;
  description?: string;
}> = [
  {
    value: "fake_profile",
    label: "Fabricated identity or financial details",
    description: "Who they are or what they reported doesn't appear genuine",
  },
  { value: "abusive_messages", label: "Abusive or inappropriate messages" },
  {
    value: "off_platform_ask",
    label: "Asked to go off-platform improperly",
    description: "Tried to skip Sill's protections",
  },
  { value: "other", label: "Something else" },
];

export const FIXTURE_THREADS: Record<string, ThreadMessageVM[]> = {
  "11111111-1111-4111-8111-111111111111": [
    {
      id: "thread-msg-1",
      clientMsgId: "thread-msg-1",
      senderRole: "system",
      body: "You're now connected with Nina & Theo Park. Say hello!",
      kind: "intro",
      createdAt: "2026-07-10T15:02:00Z",
    },
    {
      id: "thread-msg-2",
      clientMsgId: "thread-msg-2",
      senderRole: "pro",
      body: "Hi Nina — great to be working with you two. I saw the Ballard listing you ran the numbers on, want to grab 15 minutes this week to talk strategy?",
      kind: "message",
      createdAt: "2026-07-10T15:04:00Z",
    },
    {
      id: "thread-msg-3",
      clientMsgId: "thread-msg-3",
      senderRole: "consumer",
      body: "That would be great! We're free Thursday afternoon or anytime Friday.",
      kind: "message",
      createdAt: "2026-07-10T17:41:00Z",
    },
    {
      id: "thread-msg-4",
      clientMsgId: "thread-msg-4",
      senderRole: "pro",
      body: "Thursday at 2pm works on my end — I'll send a calendar invite.",
      kind: "message",
      createdAt: "2026-07-10T17:50:00Z",
    },
  ],
  // Marcus Hale — matches FIXTURE_CONVERSATIONS (his last message unread).
  "22222222-2222-4222-8222-222222222222": [
    {
      id: "thread-mh-1",
      clientMsgId: "thread-mh-1",
      senderRole: "pro",
      body: "Marcus — your lender asked for an updated pre-approval letter before Saturday's tour. Can you send it over this week?",
      kind: "message",
      createdAt: "2026-07-17T16:20:00Z",
    },
    {
      id: "thread-mh-2",
      clientMsgId: "thread-mh-2",
      senderRole: "consumer",
      body: "Thanks Jess — I'll get the updated letter over this week.",
      kind: "message",
      createdAt: "2026-07-18T09:12:00Z",
    },
  ],
  // Priya Nair — matches FIXTURE_CONVERSATIONS (pro sent last, fully read).
  "33333333-3333-4333-8333-333333333333": [
    {
      id: "thread-pn-1",
      clientMsgId: "thread-pn-1",
      senderRole: "consumer",
      body: "We're pausing the search until our lease question settles — probably a few weeks.",
      kind: "message",
      createdAt: "2026-07-11T15:02:00Z",
    },
    {
      id: "thread-pn-2",
      clientMsgId: "thread-pn-2",
      senderRole: "pro",
      body: "Sounds good, no rush on my end.",
      kind: "message",
      createdAt: "2026-07-11T15:30:00Z",
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────
// Messages inbox conversations (messaging-inbox IA WS2) — the IS_PREVIEW
// data for /messages. Rows mirror GET /api/professional/threads: client
// counterparty + last-message preview + pro-viewer unread counts. Keys line
// up with FIXTURE_CLIENTS / FIXTURE_THREADS so selecting a conversation
// renders that client's fixture thread in the pane.
// ─────────────────────────────────────────────────────────────────────────

export const FIXTURE_CONVERSATIONS: ConversationVM[] = [
  {
    threadId: "aaaa1111-0000-4000-8000-000000000001",
    profileId: "11111111-1111-4111-8111-111111111111",
    counterpartyName: "Nina & Theo Park",
    lastMessage: {
      preview:
        "That would be great! We're free Thursday afternoon or anytime Friday.",
      senderRole: "consumer",
      channel: "in_app",
      createdAt: "2026-07-19T17:41:00Z",
    },
    unreadCount: 2,
    lockedAt: null,
    createdAt: "2026-07-10T15:02:00Z",
  },
  {
    threadId: "aaaa1111-0000-4000-8000-000000000002",
    profileId: "22222222-2222-4222-8222-222222222222",
    counterpartyName: "Marcus Hale",
    lastMessage: {
      preview:
        "Thanks Jess — I'll get the updated pre-approval letter over this week.",
      senderRole: "consumer",
      channel: "in_app",
      createdAt: "2026-07-18T09:12:00Z",
    },
    unreadCount: 1,
    lockedAt: null,
    createdAt: "2026-06-28T11:00:00Z",
  },
  {
    threadId: "aaaa1111-0000-4000-8000-000000000003",
    profileId: "33333333-3333-4333-8333-333333333333",
    counterpartyName: "Priya Nair",
    lastMessage: {
      preview: "Sounds good, no rush on my end.",
      senderRole: "pro",
      channel: "in_app",
      createdAt: "2026-07-11T15:30:00Z",
    },
    unreadCount: 0,
    lockedAt: null,
    createdAt: "2026-06-20T10:00:00Z",
  },
];

// M8 hub-bindings pinned-incentive card (task 1268, D14/D21) — keyed the
// SAME way as FIXTURE_THREADS. Only Nina & Theo Park's thread (the
// marketplace-connected fixture pair) carries a pinned offer; every other
// client id misses the map, proving the "no card for an invited/CRM-only
// client" case renders identically in preview mode.
export const FIXTURE_PINNED_OFFERS: Record<string, PinnedOfferVM> = {
  "11111111-1111-4111-8111-111111111111": {
    rebate: [{ type: "commission_rebate", value: 1.5 }],
    cash: [
      { type: "closing_cost_credit", value: 1500 },
      { type: "inspection_credit", value: 400 },
    ],
  },
};

// task:hub-stage-container (plan 290 M8-A) — forces relationship_stage
// present for the SAME fixture client id, so the header chip's
// render-when-present branch has a real IS_PREVIEW fixture to exercise. The
// field is absent (map miss → undefined) for every other client id — every
// real thread today, until plan 291 ships the server-side projection.
export const FIXTURE_RELATIONSHIP_STAGES: Record<string, string> = {
  "11111111-1111-4111-8111-111111111111": "active",
};
